/**
 * Theme resolution — the small piece of logic that has to exist twice.
 *
 * The theme class must be on <html> before the first paint, and this app paints
 * unusually late: main.tsx waits for version-state init AND MSAL's
 * handleRedirectPromise() before React mounts. A theme applied from React would
 * flash the wrong colors for that entire round trip, through an empty root, a
 * loading spinner and then the app.
 *
 * So an inline <head> script in index.html and templates/spa.html does the
 * first application. That script cannot import this module — it runs before the
 * bundle exists — so the decision is duplicated there by hand. This file is the
 * canonical version, and a test asserts the inline copies are present, byte
 * identical to each other, and reading the same storage key.
 *
 * Storage is deliberately split:
 *   localStorage   the paint-time source of truth. Synchronous, available
 *                  before auth, and works on /login where no user exists yet.
 *   users/{email}  cross-device sync. Arrives later, via the permissions
 *                  listener, and reconciles into localStorage.
 */

export type ThemePreference = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

/** localStorage key. Must match the inline snippet in both HTML shells. */
export const THEME_STORAGE_KEY = 'theme';

/** The class applied to <html>. Must match the `.dark` block in styles.css. */
const DARK_CLASS = 'dark';

/**
 * Browser-UI color per theme, for <meta name="theme-color">.
 *
 * Light keeps the existing brand blue rather than the page background — it
 * shipped that way and changing it would be an unrelated visual regression.
 * Dark uses the page background (slate-950) so the browser chrome and the app
 * do not read as two different surfaces.
 */
export const THEME_COLOR: Record<ResolvedTheme, string> = {
  light: '#1e40af',
  dark: '#020618',
};

export function isThemePreference(value: unknown): value is ThemePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

/**
 * Resolve a stored preference to the theme that should actually render.
 *
 * Note the asymmetry between 'system' and an absent value — they are NOT the
 * same case, though an earlier version of this function treated them as one:
 *
 *   'system'   an explicit choice to follow the OS. Honoured.
 *   absent /   no choice has been made. Resolves to 'light', deliberately,
 *   corrupt    so that shipping dark mode does not silently re-skin the app
 *              for every user whose OS happens to be set dark. Dark is opt-in;
 *              the setting lives in Settings → Appearance.
 */
export function resolveTheme(stored: unknown, prefersDark: boolean): ResolvedTheme {
  if (stored === 'light' || stored === 'dark') return stored;
  if (stored === 'system') return prefersDark ? 'dark' : 'light';
  return 'light';
}

/**
 * Read the stored preference, tolerating disabled or corrupt storage.
 *
 * Defaults to 'light', not 'system' — see resolveTheme. A user who has never
 * touched the setting gets the interface they already had.
 */
export function readStoredTheme(): ThemePreference {
  try {
    const raw = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemePreference(raw) ? raw : 'light';
  } catch {
    // Private mode / storage disabled. 'light' is the safe default.
    return 'light';
  }
}

export function writeStoredTheme(preference: ThemePreference): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, preference);
  } catch {
    // Non-fatal: the preference simply will not survive a reload.
  }
}

/** Does the OS currently ask for dark? */
export function systemPrefersDark(): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : false;
}

/**
 * Apply a resolved theme to the document.
 *
 * Only touches <html> and the theme-color meta tags. `color-scheme` is NOT set
 * here — styles.css declares it on `:root` and `.dark`, so it follows the class
 * automatically and native controls (date pickers, scrollbars, autofill) come
 * along with it.
 */
export function applyTheme(resolved: ResolvedTheme): void {
  const root = document.documentElement;
  root.classList.toggle(DARK_CLASS, resolved === 'dark');

  for (const meta of document.querySelectorAll('meta[name="theme-color"]')) {
    meta.setAttribute('content', THEME_COLOR[resolved]);
  }
}
